import { environment } from '@environments/environment';
import { MessageTypes } from '@features/account/messaging/models';
import { Injectable, Output, EventEmitter, OnDestroy, Directive } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProxyService } from '@core/services/proxy.service';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { AlertService } from '@core/services/alert.service';
import { LoggingService } from '@core/services/logging.service';
import * as moment from 'moment-timezone';
import { CheckTimeUtility } from '@app/features/ordering/utilties/check-time-utility';

export class MessageListItem {
  constructor() { }
  id: number;
  amount: number;
  displayDateTime: string | Date;
  sortDateTime: Date;
  dateTime?: Date;
  description: string;
  expiryDateTime: Date;
  redemptionDateTime: Date;
  type: number;
  dateTimeUtc?: string;
}

@Directive()
@Injectable()
export class AccountMessagingService implements OnDestroy {

  @Output() messageList = new EventEmitter<any>();

  private alertService: AlertService;

  private subscriptions = new Subscription();
  private orderDetails = new BehaviorSubject<string>(' ');
  getOrderId = this.orderDetails.asObservable();
  private  DATE_FORMAT = environment.DATE_FORMAT;
  private groupedList = [];

  private months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  public usableRewardTypes = [
    MessageTypes.REWARDREDEEMED,
    MessageTypes.REWARDEXPIRED,
    MessageTypes.REWARDEARNED,
    MessageTypes.POINTSEARNED,
  ];

  public paymentFilterType = [
    MessageTypes.LOAD,
    MessageTypes.PAYMENT
  ];

  public hasIconType = [
    MessageTypes.REWARDREDEEMED,
    MessageTypes.REWARDEXPIRED,
    MessageTypes.REWARDEARNED,
    // MessageTypes.OFFERGIVEN,
    MessageTypes.OFFERRECEIVED,
    MessageTypes.OFFERREDEEMED
  ];

  public receiptTypes = [
    MessageTypes.ORDERPLACED,
    MessageTypes.ORDERPROCESSING,
    MessageTypes.ORDERSENTTOPOS,
    MessageTypes.ORDERCANCELLED,
    MessageTypes.VIRTUALGIFTCARDPURCHASE
  ];
  public orderTypes = [
    MessageTypes.ORDERPLACED,
    MessageTypes.ORDERPROCESSING,
    MessageTypes.ORDERSENTTOPOS,
    MessageTypes.ORDERCANCELLED
  ];

  public offerTypes = [
    // MessageTypes.OFFERGIVEN,
    MessageTypes.OFFERRECEIVED,
    MessageTypes.OFFERREDEEMED,
    MessageTypes.OFFEREXPIRED
  ];

  constructor(
    private proxy: ProxyService,
    private logger: LoggingService
  ) { }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getAccountHistoryMessages() {
    this.subscriptions.add(
      this.proxy.httpGet(ApiRoutes.ACCOUNT_HISTORY).subscribe(
        (success) => {
          // the response from history comes as an object with the reward summary and all activities as an array
          // we only care about the activities for list.
          const response = <any>success.body.valueOf();
          if (response.activities) {
            this.groupMessageList(response.activities);

            if (this.groupedList.length > 0) {
              this.messageList.emit(this.groupedList);
            } else {
              this.messageList.emit(null);
            }
          } else {
            this.messageList.emit(null);
          }
        },
        (error) => {
          this.alertService.error(error.error.message);
          this.messageList.emit(null);
          this.logger.logError(error);
        }
      )
    );
  }

  getAccountMessages() {
    this.subscriptions.add(

      this.proxy.httpGet(ApiRoutes.ACCOUNT_MESSAGES).subscribe(
        (success) => {
          // the response from history comes as an object with the reward summary and all activities as an array
          // we only care about the activities for list.
          const response = <any>success.body.valueOf();
          if (response.items) {
            this.groupMessageList(response.items);

            if (this.groupedList.length > 0) {
              this.remapMessageTypes();
              this.messageList.emit(this.groupedList);
            } else {
              this.messageList.emit(null);
            }
          } else {
            this.messageList.emit(null);
          }
        },
        (error) => {
          this.alertService.error(error.error.message);
          this.messageList.emit(null);
          this.logger.logError(error);
        }
      )
    );
  }

  setAlert(service: AlertService) {
    this.alertService = service;
  }

  deleteMessage(id: string) {
    const data = [id];
    if (this.groupedList && this.groupedList.length > 0 && this.groupedList[0] && this.groupedList[0].length > 0) {
      this.groupedList[0] = this.groupedList[0].filter((g: { id: string; }) => g.id !== id);
      this.messageList.emit(this.groupedList);
    }
    this.subscriptions.add(
      this.proxy.httpPost(ApiRoutes.MESSAGE_DELETE, data).subscribe(
        (success) => {
          if (this.groupedList && this.groupedList.length === 0 || this.groupedList[0] && this.groupedList[0].length === 0) {
            this.getAccountMessages();
          }
        },
        (error) => {
          this.getAccountMessages();
          this.alertService.error(error.error.message);
          this.logger.logError(error);
        }
      )
    );
  }

  markRead(id: string) {
    const data = [id];
    this.subscriptions.add(
      this.proxy.httpPost(ApiRoutes.MESSAGE_READ, data).subscribe(
        (success) => {
          // do nothing.
        },
        (error) => {
          this.alertService.error(error.error.message);
          this.logger.logError(error);
        }
      )
    );
  }

  remapMessageTypes() {
    for (const group of this.groupedList) {
      for (const item of group) {
        switch (item.type) {
          case 1:
            item.type = 4; // reward earned in history is 4 but 1 in messages
            break;
          case 3:
            item.type = 14;
            break;
          case 4:
            item.type = 15;
            break;
          default:
            item.type = 3;
            break;
        }
      }
    }
  }

  groupMessageList(list: Array<MessageListItem>) {
    const groups = [];

    for (const item of list) {
      this.formatDate(item);
    }

    list.sort((a, b) => {
      if (a.sortDateTime < b.sortDateTime) {
        return 1;
      } else if (a.sortDateTime > b.sortDateTime) {
        return -1;
      }
      return 0;
    });

    // go through the current array list returned by the web api of messages
    // for each item in the array look at the dateTime for when that message
    // item was generated. We want to group by Month/Year so create a group
    // name based on the Month and Year for the groups object. This group name
    // based on the month/year is going to be a key for the array of all
    // message items that are in that same month/year group
    for (const item of list) {
      const date = new Date(item.sortDateTime);
      const groupName = this.months[date.getMonth()] + ' ' + date.getFullYear();
      if (groups[groupName] === undefined) {
        groups[groupName] = [];
      }
      groups[groupName].push(item);
    }

    this.groupedList = Object.keys(groups).map((groupName) => groups[groupName]).sort(
      (a, b) => {
        const aDate = Date.parse(a[0].sortDateTime);
        const bDate = Date.parse(b[0].sortDateTime);

        if (aDate < bDate) {
          return 1;
        } else if (aDate > bDate) {
          return -1;
        }
        // Secondary filter added based on mobile order
        if (this.getSecondarySortOrder(a[0]) < this.getSecondarySortOrder(b[0])) {
          return -1;
        }
        if (this.getSecondarySortOrder(a[0]) > this.getSecondarySortOrder(b[0])) {
          return 1;
        }
        return 0;
      }
    );
  }

  getSecondarySortOrder(activityType: MessageListItem) {
    switch (activityType.type) {
      case MessageTypes.ORDERPROCESSING:
      case MessageTypes.ORDERCANCELLED:
      case MessageTypes.ORDERPLACED:
      case MessageTypes.REWARDEXPIRED:
        return 0;
      case MessageTypes.REWARDREDEEMED:
        return 1;
      case MessageTypes.REWARDEARNED:
        return 2;
      case MessageTypes.OFFERREDEEMED:
        return 3;
      case MessageTypes.OFFERRECEIVED:
        return 4;
      case MessageTypes.PAYMENT:
        return 5;
      case MessageTypes.POINTSEARNED:
        return 6;
      default:
        return 7;
    }
  }

  convertDateTime(cdate: any) {
    if (cdate) {
      const momentHour = moment(cdate).format('H');
      const momentHourInt = +momentHour;
      if (momentHourInt >= 20) {
        return moment(cdate).format(this.DATE_FORMAT);
      } else {
        return moment(cdate).subtract(1, 'd').format(this.DATE_FORMAT);
      }
    }
    return null;
  }

  formatDate(item: MessageListItem) {
    const date = new Date(item.dateTime);
    item.sortDateTime = date;
    item.displayDateTime = new Date(item.dateTime);
    switch (item.type) {
      case MessageTypes.LOAD:
      case MessageTypes.PAYMENT: {
        // item.dateTime = new Date(item.dateTime);
        break;
      }
      case MessageTypes.POINTSEARNED: {
        item.sortDateTime = this.getUTCDate(item.dateTime);
        break;
      }
      case MessageTypes.OFFERRECEIVED: {
        if (item.expiryDateTime) {
          item.displayDateTime = this.getUTCDate(item.expiryDateTime);
        } else {
          item.displayDateTime = new Date(item.dateTime);
        }
        const dateConvertion = this.convertDateTime(item.displayDateTime);
        item.displayDateTime = dateConvertion !== null ? dateConvertion : item.displayDateTime;
        break;
      }
      case MessageTypes.REWARDEARNED: {
        if (item.expiryDateTime) {
          item.displayDateTime = this.getUTCDate(item.expiryDateTime);
        } else {
          item.displayDateTime = new Date(item.dateTime);
        }
        item.sortDateTime = this.getUTCDate(item.dateTime);
        item.sortDateTime.setDate(item.sortDateTime.getDate() + 1);
        item.sortDateTime.setSeconds(item.sortDateTime.getSeconds() - 1);
        const dateConvertion = this.convertDateTime(item.displayDateTime);
        item.displayDateTime = dateConvertion !== null ? dateConvertion : item.displayDateTime;
        break;
      }
      case MessageTypes.REWARDEXPIRED: {
        if (item.expiryDateTime) {
          item.displayDateTime = this.getUTCDate(item.expiryDateTime);
          item.sortDateTime = this.getUTCDate(item.expiryDateTime);
        } else {
          item.displayDateTime = new Date(item.dateTime);
          item.sortDateTime = new Date(item.dateTime);
        }
        const dateConvertion = this.convertDateTime(item.displayDateTime);
        item.displayDateTime = dateConvertion !== null ? dateConvertion : item.displayDateTime;
        break;
      }
      case MessageTypes.REWARDREDEEMED: 
      case MessageTypes.OFFERREDEEMED: {
        if (item.redemptionDateTime) {
          item.displayDateTime = this.getUTCDate(item.redemptionDateTime);
          item.sortDateTime = this.getUTCDate(item.redemptionDateTime);
        } else {
          item.displayDateTime = new Date(item.dateTime);
          item.sortDateTime = new Date(item.dateTime);
        }
        break;
      }
      case MessageTypes.VIRTUALGIFTCARDPURCHASE: {
        const localDateTime = CheckTimeUtility.getLocalFromUtc(item.dateTimeUtc);        
        item.displayDateTime = localDateTime?.displayDateTime;
        item.dateTime = localDateTime?.dateTime as any;
        item.sortDateTime = new Date(item.dateTimeUtc);
        break;
      }
      case MessageTypes.ORDERPLACED:
      case MessageTypes.ORDERPROCESSING: {
        break;
      }
      default: {
        break;
      }
    }
  }

  findOrderId(orderdt: number, type: number) {
    const orderObj = {
      'orderdt': orderdt,
      'type': type,
    };
    const ordDetails = JSON.stringify(orderObj);
    this.orderDetails.next(ordDetails);
  }

  getUTCDate(date: Date) {
    const dateLocal = new Date(date);
    return new Date(dateLocal.getUTCFullYear(),
      dateLocal.getUTCMonth(),
      dateLocal.getUTCDate(),
      dateLocal.getUTCHours(),
      dateLocal.getUTCMinutes(),
      dateLocal.getUTCSeconds()
    );
  }

  isRewardMsg(item: any): boolean {
    return item.type === MessageTypes.REWARDEARNED;
  }
  isOfferExpired(item: any): boolean {
    return item.type === MessageTypes.OFFEREXPIRED;
  }

  isExternalOrMenuItemMsg(item: any): boolean {
    return item.payload &&
      (item.payload.includes('http') ||
       (item.payload.toLowerCase().includes('categoryid') &&
       item.payload.toLowerCase().includes('parentrecipeid')));
  }

}
